import { useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, TrendingDown, Beaker, Printer, Droplets } from "lucide-react";

const MONTH_NAMES = [
  "Janvier","Février","Mars","Avril","Mai","Juin",
  "Juillet","Août","Septembre","Octobre","Novembre","Décembre",
];

const formatMonth = (yyyymm) => {
  if (!yyyymm) return "Toutes périodes";
  const [y, m] = yyyymm.split("-");
  return `${MONTH_NAMES[Number(m) - 1]} ${y}`;
};

const MEDALS = ["🥇", "🥈", "🥉"];
const MEDAL_BG = [
  "bg-gradient-to-br from-yellow-500/20 via-yellow-500/5 to-transparent border-yellow-500/40",
  "bg-gradient-to-br from-zinc-400/15 via-zinc-400/5 to-transparent border-zinc-400/40",
  "bg-gradient-to-br from-orange-700/20 via-orange-700/5 to-transparent border-orange-700/40",
];
const MEDAL_TEXT = ["text-yellow-400", "text-zinc-300", "text-orange-400"];

export default function Stats() {
  const [selectedMonth, setSelectedMonth] = useState("current");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const currentMonth = new Date().toISOString().slice(0, 7);
    const monthParam = selectedMonth === "all" ? "all" : (selectedMonth === "current" ? currentMonth : selectedMonth);
    api.get(`/stats/overview?month=${monthParam}`).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [selectedMonth]);

  const ranking = data?.flavor_ranking || [];
  const podium = ranking.slice(0, 3);
  const rest = ranking.slice(3);

  const monthOptions = useMemo(() => {
    const opts = [{ value: "current", label: "Ce mois-ci" }, { value: "all", label: "Toutes périodes" }];
    (data?.available_months || []).forEach((m) => {
      opts.push({ value: m, label: formatMonth(m) });
    });
    // Dedupe
    const seen = new Set();
    return opts.filter((o) => !seen.has(o.value) && seen.add(o.value));
  }, [data?.available_months]);

  const totalBottlesInPeriod = ranking.reduce((s, f) => s + f.bottles, 0);
  const totalVolumeInPeriod = ranking.reduce((s, f) => s + f.volume_ml, 0);
  const totalLabelsInPeriod = ranking.reduce((s, f) => s + (f.labels || 0), 0);

  return (
    <DashboardLayout title="Statistiques de vente">
      {/* Month filter + summary */}
      <div className="mb-6 grid grid-cols-1 lg:grid-cols-12 gap-0 border border-zinc-800">
        <div className="lg:col-span-4 p-6 border-b lg:border-b-0 lg:border-r border-zinc-800">
          <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Période</label>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger data-testid="stats-month-select" className="rounded-none bg-zinc-900 border-zinc-800 h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 rounded-none max-h-80">
              {monthOptions.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`stats-month-${o.value}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="lg:col-span-8 grid grid-cols-3">
          <MiniStat label="Étiquettes" value={totalLabelsInPeriod.toLocaleString("fr-FR")} icon={Printer} testid="stat-labels" />
          <MiniStat label="Flacons" value={totalBottlesInPeriod.toLocaleString("fr-FR")} icon={Beaker} testid="stat-bottles" />
          <MiniStat label="Volume total" value={`${(totalVolumeInPeriod / 1000).toFixed(1)} L`} icon={Droplets} testid="stat-volume" />
        </div>
      </div>

      {loading && (
        <div className="border border-zinc-800 py-24 text-center text-zinc-500 text-sm">Chargement…</div>
      )}

      {!loading && ranking.length === 0 && (
        <div className="border border-zinc-800 py-24 text-center">
          <div className="font-display text-xl font-bold text-white mb-2">Aucune vente sur cette période</div>
          <div className="text-sm text-zinc-500">Commencez à imprimer des étiquettes pour voir vos parfums les plus vendus.</div>
        </div>
      )}

      {!loading && podium.length > 0 && (
        <>
          {/* Podium (top 3 with medals) */}
          <h2 className="font-display text-2xl font-bold tracking-tight mb-4">
            Podium des parfums · <span className="text-zinc-500 font-normal text-lg">{selectedMonth === "current" ? "Ce mois-ci" : selectedMonth === "all" ? "Toutes périodes" : formatMonth(selectedMonth)}</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
            {podium.map((f, i) => (
              <div key={f.brand + f.flavor} data-testid={`podium-rank-${i + 1}`} className={`border p-6 relative ${MEDAL_BG[i]}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Rang #{i + 1}</div>
                    <div className="font-display text-xl font-bold text-white leading-tight">{f.brand || "—"}</div>
                    <div className="text-sm text-zinc-400 mt-0.5 leading-tight">{f.flavor || "—"}</div>
                  </div>
                  <div className="text-4xl leading-none" title={`Médaille ${["or", "argent", "bronze"][i]}`}>{MEDALS[i]}</div>
                </div>
                <div className="mt-6 flex items-baseline gap-2 border-t border-zinc-800 pt-4">
                  <div className={`font-display text-4xl font-extrabold tracking-tighter ${MEDAL_TEXT[i]}`}>
                    {f.bottles.toLocaleString("fr-FR")}
                  </div>
                  <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest">flacon{f.bottles > 1 ? "s" : ""}</div>
                </div>
                <div className="text-[11px] text-zinc-500 font-mono mt-1">
                  {(f.volume_ml / 1000).toFixed(1)} L · {f.labels} impression{f.labels > 1 ? "s" : ""}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {!loading && rest.length > 0 && (
        <>
          <h2 className="font-display text-lg font-bold tracking-tight mb-3">
            Classement complet — <span className="text-zinc-500 font-normal">{ranking.length} parfum{ranking.length > 1 ? "s" : ""}</span>
          </h2>
          <div className="border border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 border-b border-zinc-800">
                <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                  <th className="px-4 py-3 w-16">Rang</th>
                  <th className="px-4 py-3">Marque</th>
                  <th className="px-4 py-3">Parfum</th>
                  <th className="px-4 py-3 text-right">Flacons</th>
                  <th className="px-4 py-3 text-right">Volume</th>
                  <th className="px-4 py-3 text-right">Étiquettes</th>
                </tr>
              </thead>
              <tbody>
                {rest.map((f) => (
                  <tr key={f.brand + f.flavor} data-testid={`ranking-row-${f.rank}`} className="border-b border-zinc-800 last:border-b-0 hover:bg-zinc-900/40">
                    <td className="px-4 py-3 font-mono text-sm text-zinc-500">#{f.rank}</td>
                    <td className="px-4 py-3 text-white font-semibold">{f.brand || "—"}</td>
                    <td className="px-4 py-3 text-zinc-300">{f.flavor || "—"}</td>
                    <td className="px-4 py-3 font-mono text-sm text-white text-right">{f.bottles.toLocaleString("fr-FR")}</td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-400 text-right">{(f.volume_ml / 1000).toFixed(1)} L</td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-400 text-right">{f.labels}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Growth insight */}
      {!loading && data && data.growth_pct !== null && data.growth_pct !== undefined && selectedMonth === "current" && (
        <div className="mt-8 border border-zinc-800 p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-500 mb-2">Évolution</div>
          <div className="flex items-baseline gap-4 flex-wrap">
            <div>
              <div className={`font-display text-4xl font-extrabold tracking-tighter flex items-center gap-2 ${data.growth_pct >= 0 ? "text-green-400" : "text-red-400"}`}>
                {data.growth_pct >= 0 ? <TrendingUp className="w-8 h-8" /> : <TrendingDown className="w-8 h-8" />}
                {data.growth_pct >= 0 ? "+" : ""}{data.growth_pct}%
              </div>
              <div className="text-xs text-zinc-500 font-mono mt-1">vs mois dernier</div>
            </div>
            <div className="text-sm text-zinc-400 border-l border-zinc-800 pl-4">
              <div><b className="text-white">{data.bottles_this_month}</b> flacons ce mois · <b className="text-white">{data.bottles_last_month}</b> flacons le mois dernier</div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function MiniStat({ label, value, icon: Icon, testid }) {
  return (
    <div className="[&:not(:first-child)]:border-l border-zinc-800 p-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 text-red-500" />
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">{label}</div>
      </div>
      <div className="font-display text-3xl font-extrabold tracking-tighter mt-2 text-white">{value}</div>
    </div>
  );
}
